import React, { useState } from "react";
import {
  Save,
  AlertCircle,
  Calendar,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";

const DateScheduler = ({ selectedSchedule, schedulesService, onScheduleUpdate, onClose }) => {
  const [dates, setDates] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleChange = (weekIndex, gameIndex, field, value) => {
    setDates((prev) => ({
      ...prev,
      [`${weekIndex}-${gameIndex}`]: {
        ...prev[`${weekIndex}-${gameIndex}`],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      const updated = [...selectedSchedule.schedule_data];
      Object.entries(dates).forEach(([key, d]) => {
        const [wi, gi] = key.split("-").map(Number);
        updated[wi].games[gi] = { ...updated[wi].games[gi], ...d };
      });
      
      await schedulesService.update(selectedSchedule.id, {
        gameEdit: true,
        scheduleData: updated,
      });
      
      setShowSuccessMessage(true);
      setDates({});
      await onScheduleUpdate();
      
      setTimeout(() => setShowSuccessMessage(false), 2000);
    } catch (err) {
      setError("Failed to save dates/times");
    } finally {
      setSaving(false);
    }
  };

  const toggleWeek = (weekIndex) => {
    setExpandedWeeks((prev) => ({
      ...prev,
      [weekIndex]: !prev[weekIndex],
    }));
  };

  const hasChanges = Object.keys(dates).length > 0;

  return (
    <div style={{ 
      width: '100%', 
      height: '600px', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '8px',
      position: 'relative'
    }}>
      {/* Success Message */}
      {showSuccessMessage && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: '500' }}>Dates saved successfully!</span>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Calendar size={24} color="#3b82f6" />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Schedule Dates & Times</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {hasChanges && (
            <div style={{
              backgroundColor: '#fef3c7',
              color: '#92400e',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {Object.keys(dates).length} unsaved changes
            </div>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
              color: '#6b7280'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Instruction */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#eff6ff',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '14px',
        color: '#1e40af'
      }}>
        Set dates and times for each game. Changes are highlighted in yellow.
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          margin: '16px 20px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Games List - Scrollable */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px'
      }}>
        <div style={{ display: 'grid', gap: '20px' }}>
          {selectedSchedule.schedule_data.map((week, wi) => {
            const isExpanded = expandedWeeks[wi] !== false;
            const unsavedCount = week.games.filter((_, gi) => dates[`${wi}-${gi}`]).length;

            return (
              <div key={wi} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'white'
              }}>
                {/* Week Header */}
                <button
                  onClick={() => toggleWeek(wi)}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    backgroundColor: '#f9fafb',
                    border: 'none',
                    borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      Week {week.week}
                    </div>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      {week.games.length} games
                    </span>
                    {unsavedCount > 0 && (
                      <div style={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {unsavedCount} unsaved
                      </div>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                {/* Games */}
                {isExpanded && (
                  <div style={{ padding: '16px' }}>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {week.games.map((g, gi) => {
                        const key = `${wi}-${gi}`;
                        const changes = dates[key];
                        const isComplete = (changes?.date || g.date) && (changes?.time || g.time);
                        const modified = !!changes;

                        return (
                          <div
                            key={gi}
                            style={{
                              padding: '16px',
                              border: modified ? '2px solid #fbbf24' : '1px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: modified ? '#fffbeb' : isComplete ? '#f0fdf4' : 'white',
                              transition: 'all 0.2s',
                              position: 'relative'
                            }}
                          >
                            {/* Status Badge */}
                            {modified && (
                              <div style={{
                                position: 'absolute',
                                top: '-10px',
                                right: '16px',
                                backgroundColor: '#fbbf24',
                                color: 'white',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <Clock size={12} />
                                MODIFIED
                              </div>
                            )}
                            {!modified && isComplete && (
                              <div style={{
                                position: 'absolute',
                                top: '-10px',
                                right: '16px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <CheckCircle size={12} />
                                SCHEDULED
                              </div>
                            )}

                            {/* Teams */}
                            <div style={{ 
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              flexWrap: 'wrap'
                            }}>
                              <span style={{
                                fontWeight: '600',
                                color: '#111827',
                                fontSize: '15px'
                              }}>
                                {g.homeTeam}
                              </span>
                              <span style={{
                                color: '#6b7280',
                                fontWeight: '500',
                                fontSize: '14px'
                              }}>
                                vs
                              </span>
                              <span style={{
                                fontWeight: '600',
                                color: '#111827',
                                fontSize: '15px'
                              }}>
                                {g.awayTeam}
                              </span>
                            </div>

                            {/* Date & Time Inputs */}
                            <div style={{ 
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                              gap: '12px'
                            }}>
                              {/* Date Input */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  color: '#6b7280',
                                  marginBottom: '6px'
                                }}>
                                  Date
                                </label>
                                <div style={{ position: 'relative' }}>
                                  <Calendar 
                                    size={16} 
                                    style={{
                                      position: 'absolute',
                                      left: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#9ca3af',
                                      pointerEvents: 'none'
                                    }}
                                  />
                                  <input
                                    type="date"
                                    value={changes?.date || g.date || ""}
                                    onChange={(e) => handleChange(wi, gi, "date", e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '8px 8px 8px 34px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '14px',
                                      boxSizing: 'border-box',
                                      transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                  />
                                </div>
                              </div>

                              {/* Time Input */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  color: '#6b7280',
                                  marginBottom: '6px'
                                }}>
                                  Time
                                </label>
                                <div style={{ position: 'relative' }}>
                                  <Clock 
                                    size={16} 
                                    style={{
                                      position: 'absolute',
                                      left: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#9ca3af',
                                      pointerEvents: 'none'
                                    }}
                                  />
                                  <input
                                    type="time"
                                    value={changes?.time || g.time || ""}
                                    onChange={(e) => handleChange(wi, gi, "time", e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '8px 8px 8px 34px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '14px',
                                      boxSizing: 'border-box',
                                      transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating Save Button */}
      {hasChanges && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px 20px',
          backgroundColor: 'white',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setDates({})}
            disabled={saving}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              backgroundColor: 'white',
              color: '#374151',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!saving) e.target.style.backgroundColor = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            Cancel Changes
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              backgroundColor: saving ? '#93c5fd' : '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              opacity: saving ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!saving) e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (!saving) e.target.style.backgroundColor = '#3b82f6';
            }}
          >
            <Save size={16} />
            {saving ? 'Saving...' : `Save ${Object.keys(dates).length} Changes`}
          </button>
        </div>
      )}
    </div>
  );
};

export default DateScheduler;